<template>
  <!-- Range control for the index chart.
       Was <ul><li @click>, with no role, no tabindex and no button, so there was
       no keyboard path to change the chart range at all. A tablist with roving
       tabindex is the pattern for a segmented control selecting one of N views of
       the same data: Tab reaches the control once, arrows move within it. -->
  <div class="date-filter" role="tablist" aria-label="Chart date range">
    <button
        v-for="([keyValue, filter], i) in filters"
        :key="keyValue"
        ref="tabs"
        type="button"
        role="tab"
        :aria-selected="isActive(filter) ? 'true' : 'false'"
        :tabindex="isActive(filter) ? 0 : -1"
        :class="{ active: isActive(filter) }"
        @click="$emit('filter', filter)"
        @keydown="onKeydown($event, i)"
    >
      {{ keyValue }}
    </button>
  </div>
</template>

<style scoped>
  /* Segmented control. These were text-green-200, which after the light-mode
     flip resolved to near-white on a white ground and became unreadable. */
  /* flex + w-fit, not inline-flex: mx-auto cannot centre an inline-level box, which
     is why this sat hard left above a full-width chart. */
  /* nowrap, not wrap: wrapping dropped "All" onto its own second row on phones.
     The tighter phone padding below fits all eight segments on one line; the
     max-width plus overflow is the safety net for very narrow screens. */
  .date-filter {
    @apply flex w-fit flex-row flex-nowrap justify-center items-center gap-1 mx-auto rounded-lg p-1 overflow-x-auto;
    /* max-w-full is not in this project's Tailwind maxWidth scale. */
    max-width: 100%;
    background: rgb(var(--c-surface-higher));
    border: 1px solid var(--hairline);
  }

  .date-filter button {
    @apply font-medium cursor-pointer rounded-md transition-colors duration-200 select-none whitespace-nowrap;
    color: rgb(var(--c-on-ground-muted));
    font-size: 13px;
    letter-spacing: 0.04em;
    padding: 0.45rem 0.5rem;
    /* 44px minimum touch target. These measured 39px tall. */
    min-height: 44px;
    background: none;
    border: 0;

    &:hover {
      color: rgb(var(--c-on-ground));
      background: rgb(var(--c-surface));
    }

    &.active {
      background: rgb(var(--c-brand));
      color: #fff;
    }
  }

  @screen sm {
    .date-filter button {
      font-size: 14px;
      padding: 0.5rem 1.1rem;
    }
  }
</style>

<script>
export default {
  props: {
    active: Object,
    filters: Map
  },
  emits: ['filter'],

  methods: {
    isActive(filter) {
      return JSON.stringify(this.active) === JSON.stringify(filter);
    },

    /* Arrow keys move between tabs and select as they go, which is the expected
     * behaviour for a tablist whose panels are cheap to render. Home/End jump to
     * the ends. Everything else is left to the browser. */
    onKeydown(event, index) {
      const entries = Array.from(this.filters);
      const last = entries.length - 1;

      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = index === last ? 0 : index + 1;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = index === 0 ? last : index - 1;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = last;
      }

      if (next === null) {
        return;
      }

      event.preventDefault();
      this.$emit('filter', entries[next][1]);

      /* Focus follows selection, otherwise the next arrow press would move from
       * wherever focus was left rather than from the tab now shown as selected. */
      this.$nextTick(() => {
        const buttons = this.$refs.tabs;
        if (buttons && buttons[next]) {
          buttons[next].focus();
        }
      });
    },
  },
}
</script>
